import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  FileText, CheckCircle, Users, MessageSquare, Clock, Edit3, Download,
  UserPlus, Play, Settings, ArrowRight, MapPin, Building2,
  ChevronDown, XCircle, LayoutGrid, Search, Upload,
  ChevronUp, ChevronsUpDown, Trash2, MoveRight, Loader2, Check, X, AlertCircle,
} from 'lucide-react'
import { ExportModal } from '@/components/ui/export-modal'
import { ErrorFallback } from '@/components/common/ErrorFallback'
import { useEngineRequest } from '@/hooks/useEngineRequest'
import { engineApiClient, apiRequest } from '@/lib/apiClient'
import { engineEndpoints } from '@/lib/contracts'
import type { SearchSprintStatus, CandidateListResponse, OutreachStatus } from '@/lib/contracts'

import { SourceBadge, ScoreBandBadge } from '@/components/common/CandidateBadges'
import { HeadhuntPanel } from '@/components/common/HeadhuntPanel'

const FALLBACK_PROJECT_ID = 'alpha-grid'

// ─── Pipeline stages ──────────────────────────────────────────────────────────

const PIPELINE_STAGE_META = [
  { label: 'Identified',  desc: 'Candidates sourced',  border: 'border-[#FD802E]/40',   text: 'text-[#FD802E]',   bg: 'bg-[#FD802E]/10',   icon: Users },
  { label: 'Outreach',    desc: 'Contacted',            border: 'border-blue-500/40',    text: 'text-blue-400',    bg: 'bg-blue-500/10',    icon: MessageSquare },
  { label: 'Shortlisted', desc: 'Under review',         border: 'border-yellow-500/40',  text: 'text-yellow-400',  bg: 'bg-yellow-500/10',  icon: CheckCircle },
  { label: 'Interview',   desc: 'Active interviews',    border: 'border-green-500/40',   text: 'text-green-400',   bg: 'bg-green-500/10',   icon: Play },
] as const

const STAGE_LABELS = ['Identified', 'Outreach', 'Shortlisted', 'Interview'] as const
type CandidateStage = typeof STAGE_LABELS[number]

const STAGE_PILL: Record<CandidateStage, string> = {
  Identified:  'bg-[#FD802E]/10 text-[#FD802E]',
  Outreach:    'bg-blue-500/10 text-blue-400',
  Shortlisted: 'bg-yellow-500/10 text-yellow-400',
  Interview:   'bg-green-500/10 text-green-400',
}

// ─── Candidate data ───────────────────────────────────────────────────────────

type Candidate = {
  id:        string
  initials:  string
  name:      string
  title:     string
  company:   string
  location:  string
  match:     number
  prescreen: boolean
  stage:     CandidateStage
  rejected:  boolean
  source?:          string
  scoreBand?:       string
  linkedinUrl?:     string
  cvUrl?:           string
  summary?:         string
  locationText?:    string
  outreachStatus?:  OutreachStatus
  createdAt?:       string  // ISO timestamp for "New" badge detection
}

// ─── New-candidate helper ─────────────────────────────────────────────────────

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000

const isNewCandidate = (createdAt?: string): boolean => {
  if (!createdAt) return false
  return Date.now() - new Date(createdAt).getTime() < THREE_DAYS_MS
}

// ─── Pre-screen status helpers ────────────────────────────────────────────────

function getPreScreenInfo(status?: OutreachStatus): { label: string; className: string } {
  if (!status || status === 'not_started' || status === 'prepared') {
    return { label: '—', className: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-500 border border-gray-500/20' }
  }
  if (status === 'pre_screen_completed') {
    return { label: 'Completed', className: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20' }
  }
  if (status === 'pre_screen_started' || status === 'link_clicked') {
    return { label: 'Started', className: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20' }
  }
  if (status === 'sent' || status === 'email_opened') {
    return { label: 'Sent', className: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20' }
  }
  return { label: '→', className: 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-500 border border-gray-500/20' }
}

// No initial/mock candidates — the table starts empty and is populated exclusively
// from the live API (uploaded CSV, applied candidates, or Headhunt sourced candidates).

// ─── Static data ──────────────────────────────────────────────────────────────

const SEARCH_OPEN_DAYS = 8

const OUTPUTS = [
  'Full Job Description',
  'Executive Summary',
  'Search Brief',
  'Interview Scorecard',
  'Outreach Brief',
]

// ─── Match badge ──────────────────────────────────────────────────────────────

const MatchBadge = ({ score }: { score: number }) => {
  const color =
    score >= 80 ? 'text-green-400 border-green-500/40' :
    score >= 65 ? 'text-[#FD802E] border-[#FD802E]/40' :
                  'text-gray-400 border-gray-500/40'
  return (
    <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-2 font-bold text-base ${color}`}>
      {score}
    </span>
  )
}

// ─── Stage selector dropdown ──────────────────────────────────────────────────

const StageSelector = ({
  current,
  onChange,
}: {
  current: CandidateStage
  onChange: (stage: CandidateStage) => void
}) => {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${STAGE_PILL[current]}`}
      >
        {current}
        <ChevronDown size={10} className="shrink-0" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-[#1a2e3d] border border-[#2a4a5c] rounded-lg py-1 min-w-[130px] shadow-xl">
            {STAGE_LABELS.map((s) => (
              <button
                key={s}
                onClick={() => { onChange(s); setOpen(false) }}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[#233D4C] transition-colors ${
                  s === current ? 'opacity-50 cursor-default' : ''
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${
                  s === 'Identified'  ? 'bg-[#FD802E]'   :
                  s === 'Outreach'    ? 'bg-blue-400'    :
                  s === 'Shortlisted' ? 'bg-yellow-400'  :
                                        'bg-green-400'
                }`} />
                <span className="text-white">{s}</span>
                {s === current && <span className="ml-auto text-gray-500">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ProjectDashboard = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const navigate  = useNavigate()

  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [candidatesLoading, setCandidatesLoading] = useState(true)
  const [candidatesError, setCandidatesError] = useState(false)
  const [rejectConfirm, setRejectConfirm] = useState<string | null>(null)
  const [showExport, setShowExport] = useState(false)
  const [searchStatus, setSearchStatus] = useState<SearchSprintStatus>('complete')
  const [isLaunchingSearch, setIsLaunchingSearch] = useState(false)
  const [candidateListKey, setCandidateListKey] = useState(0)

  // Fetch live candidate list + outreach state in parallel — merge outreach status onto each candidate
  useEffect(() => {
    setCandidatesLoading(true)
    setCandidatesError(false)
    Promise.all([
      engineApiClient.listCandidates(projectId),
      engineApiClient.listOutreach(projectId).catch(() => ({ projectId, outreach: [] })),
    ]).then(([res, outreachRes]: [CandidateListResponse, { projectId: string; outreach: { candidateId: string; status: OutreachStatus }[] }]) => {
        const outreachMap = new Map<string, OutreachStatus>(
          outreachRes.outreach.map((o) => [o.candidateId, o.status])
        )
        setCandidates(
          res.candidates.map((c) => {
            // Sourced candidates store "Role Title, Company Name" in one field — split cleanly
            const rawTitle  = c.candidateTitle ?? ''
            const commaIdx  = rawTitle.indexOf(', ')
            const roleTitle = commaIdx > -1 ? rawTitle.slice(0, commaIdx) : rawTitle
            const company   = commaIdx > -1 ? rawTitle.slice(commaIdx + 2) : ''
            // Use locationText (dedicated field) — fall back to summary regex for older rows
            const locationText = (c as unknown as { locationText?: string }).locationText
            const summaryText  = (c as unknown as { summary?: string }).summary ?? ''
            const locationMatch = !locationText ? summaryText.match(/^Based in ([^.]+)\./) : null
            const location = locationText ?? (locationMatch ? locationMatch[1] : '')
            // Map API pipelineStage to local CandidateStage (capitalised)
            const stageMap: Record<string, CandidateStage> = {
              identified: 'Identified', outreach: 'Outreach',
              shortlisted: 'Shortlisted', interview: 'Interview',
            }
            const stage = stageMap[(c.pipelineStage ?? 'identified').toLowerCase()] ?? 'Identified'
            return {
              id:             c.candidateId,
              initials:       c.candidateName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
              name:           c.candidateName,
              title:          roleTitle,
              company,
              location,
              match:          c.fitScore,
              prescreen:      false,
              stage,
              rejected:       false,
              source:         c.source,
              scoreBand:      c.scoreBand,
              linkedinUrl:    c.linkedinUrl,
              cvUrl:          c.cvUrl,
              summary:        summaryText,
              outreachStatus: outreachMap.get(c.candidateId),
              createdAt:      c.created_at,
            }
          })
        )
      })
      .catch(() => { setCandidatesError(true) })
      .finally(() => { setCandidatesLoading(false) })
  }, [projectId, candidateListKey])

  // Callback for HeadhuntPanel — triggers candidate list re-fetch
  const handleCandidatesAdded = useCallback(() => {
    setCandidateListKey((k) => k + 1)
  }, [])

  // Load search sprint status on mount
  useEffect(() => {
    engineApiClient.getSearchStatus(projectId)
      .then((s) => setSearchStatus(s.status))
      .catch(() => setSearchStatus('complete')) // default to complete so demo works
  }, [projectId])

  const handleLaunchSearch = async () => {
    setIsLaunchingSearch(true)
    setSearchStatus('in_progress')
    try {
      const res = await engineApiClient.launchSearch(projectId)
      setSearchStatus(res.sprint.status)
    } catch {
      setSearchStatus('failed')
    } finally {
      setIsLaunchingSearch(false)
    }
  }

  const exportContent = OUTPUTS.map(o => `# ${o}\n\n[Generated content for ${o}]`).join('\n\n---\n\n')

  const { error, refetch } = useEngineRequest(
    () => engineApiClient.getDashboardSummary(projectId),
    [projectId],
  )

  const approveForOutreach = (candidateId: string) => {
    // Optimistically update stage
    setCandidates((prev) =>
      prev.map((c) => (c.id === candidateId ? { ...c, stage: 'Outreach' } : c))
    )
    // Persist stage
    apiRequest({ path: engineEndpoints.candidateStage(projectId, candidateId), method: 'PATCH', body: { stage: 'outreach' } })
      .catch(() => { /* non-fatal */ })
    // Fire-and-forget: generate outreach draft in background
    engineApiClient.generateOutreach(projectId, candidateId)
      .catch(() => { /* non-fatal */ })
  }

  const moveStage = (candidateId: string, stage: CandidateStage) => {
    // Optimistic update
    setCandidates((prev) =>
      prev.map((c) => (c.id === candidateId ? { ...c, stage } : c))
    )
    // Persist to backend — fire and forget (optimistic is enough for MVP2)
    apiRequest({ path: engineEndpoints.candidateStage(projectId, candidateId), method: 'PATCH', body: { stage: stage.toLowerCase() } })
      .catch(() => { /* non-fatal — stage shown correctly locally even if persist fails */ })
  }

  const rejectCandidate = (candidateId: string) => {
    setCandidates((prev) =>
      prev.map((c) => (c.id === candidateId ? { ...c, rejected: true } : c))
    )
    setRejectConfirm(null)
  }

  // ── CV upload state ─────────────────────────────────────────────────────────
  const cvInputRef = useRef<HTMLInputElement>(null)
  const [cvUploading, setCvUploading] = useState(false)
  const [cvToast, setCvToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const handleCvFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset input so same file can be re-selected
    if (cvInputRef.current) cvInputRef.current.value = ''
    setCvUploading(true)
    setCvToast(null)
    try {
      const result = await engineApiClient.uploadCandidateCV(projectId, file)
      const name = result.candidate?.full_name ?? 'Candidate'
      setCvToast({ type: 'success', message: `✓ ${name} added to pipeline` })
      setCandidateListKey((k) => k + 1) // refresh candidate list
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed'
      setCvToast({ type: 'error', message: msg })
    } finally {
      setCvUploading(false)
      setTimeout(() => setCvToast(null), 5000)
    }
  }

  const [stageFilter, setStageFilter] = useState<'all' | 'identified' | 'shortlisted' | 'interview'>('all')
  const [sourceFilter, setSourceFilter] = useState<'all' | 'applied' | 'sourced' | 'uploaded'>('all')
  const [showExportShortlist, setShowExportShortlist] = useState(false)
  const [finaliseSending, setFinaliseSending] = useState(false)
  const [finaliseToast, setFinaliseToast] = useState<string | null>(null)

  // ── Export helpers ──────────────────────────────────────────────────────────
  const escapeCSV = (val: string | number | undefined) => {
    const s = String(val ?? '')
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
  }

  const generateCSV = (rows: Candidate[]) => {
    const headers = ['Rank', 'Name', 'Current Role', 'Location', 'Fit Score', 'Score Band', 'Pre-Screen Status', 'Match Rationale']
    const lines = [
      headers.join(','),
      ...rows.map((c, i) => {
        const { label: psLabel } = getPreScreenInfo(c.outreachStatus)
        return [
          escapeCSV(i + 1),
          escapeCSV(c.name),
          escapeCSV([c.title, c.company].filter(Boolean).join(' @ ')),
          escapeCSV(c.location),
          escapeCSV(c.match),
          escapeCSV(c.scoreBand ?? ''),
          escapeCSV(psLabel),
          escapeCSV(c.summary ?? ''),
        ].join(',')
      }),
    ]
    return lines.join('\n')
  }

  const exportAsCSV = () => {
    setShowExportShortlist(false)
    const csv = generateCSV(filteredCandidates)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const date = new Date().toISOString().slice(0, 10)
    const a = document.createElement('a')
    a.href = url
    a.download = `searchline-shortlist-${projectId}-${date}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const exportAsPDF = () => {
    setShowExportShortlist(false)
    // Brief timeout to let dropdown close before print dialog opens
    setTimeout(() => window.print(), 100)
  }

  // ── Sort ────────────────────────────────────────────────────────────────────
  type SortKey = 'match' | 'scoreBand' | 'source' | 'stage' | null
  type SortDir = 'asc' | 'desc'
  const [sortKey, setSortKey] = useState<SortKey>(null)
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const SCORE_BAND_ORDER: Record<string, number> = { strong_fit: 0, qualify: 1, review: 2, low_fit: 3, '': 4 }
  const PRESCREEN_ENGAGE_ORDER: Record<string, number> = {
    pre_screen_completed: 0,
    pre_screen_started: 1,
    link_clicked: 1,
    sent: 2,
    email_opened: 2,
  }
  const SOURCE_ORDER: Record<string, number>     = { sourced: 0, applied: 1, uploaded: 2 }
  const STAGE_ORDER: Record<string, number>      = { Interview: 0, Shortlisted: 1, Outreach: 2, Identified: 3 }

  // ── Selection ───────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const toggleSelect = (id: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const toggleSelectAll = (ids: string[]) =>
    setSelected((prev) => ids.every((id) => prev.has(id)) ? new Set() : new Set(ids))

  // ── Bulk action state ───────────────────────────────────────────────────────
  const [bulkStageOpen,   setBulkStageOpen]   = useState(false)
  const [bulkWorking,     setBulkWorking]      = useState(false)
  const [confirmBulkDel,  setConfirmBulkDel]   = useState(false)

  const bulkMoveStage = async (stage: CandidateStage) => {
    setBulkStageOpen(false)
    setBulkWorking(true)
    const ids = Array.from(selected)
    // Optimistic
    setCandidates((prev) => prev.map((c) => ids.includes(c.id) ? { ...c, stage } : c))
    setSelected(new Set())
    try {
      await apiRequest({
        path:   engineEndpoints.candidatesBulkStage(projectId),
        method: 'POST',
        body:   { candidateIds: ids, stage: stage.toLowerCase() },
      })
    } catch { /* optimistic — local state already updated */ }
    setBulkWorking(false)
  }

  const bulkDelete = async () => {
    setConfirmBulkDel(false)
    setBulkWorking(true)
    const ids = Array.from(selected)
    // Optimistic: mark as rejected (same as single reject)
    setCandidates((prev) => prev.map((c) => ids.includes(c.id) ? { ...c, rejected: true } : c))
    setSelected(new Set())
    try {
      await apiRequest({
        path:   engineEndpoints.candidatesBulkDelete(projectId),
        method: 'DELETE',
        body:   { candidateIds: ids },
      })
    } catch { /* optimistic */ }
    setBulkWorking(false)
  }

  const activeCandidates = candidates.filter((c) => !c.rejected)

  const sortedFiltered = (() => {
    let list = activeCandidates.filter((c) => {
      // Stage filter takes priority over source filter
      if (stageFilter !== 'all') {
        if (stageFilter === 'identified') return c.stage === 'Identified'
        if (stageFilter === 'shortlisted') return c.stage === 'Shortlisted'
        if (stageFilter === 'interview') return c.stage === 'Interview'
      }
      // Source filter only when no stage filter active
      if (sourceFilter === 'all') return true
      return (c.source ?? 'uploaded') === sourceFilter
    })
    if (!sortKey) {
      // Default smart sort: score band → pre-screen engagement → raw fit score
      list = [...list].sort((a, b) => {
        const bandA = SCORE_BAND_ORDER[a.scoreBand ?? ''] ?? 3
        const bandB = SCORE_BAND_ORDER[b.scoreBand ?? ''] ?? 3
        if (bandA !== bandB) return bandA - bandB
        const psA = PRESCREEN_ENGAGE_ORDER[a.outreachStatus ?? ''] ?? 3
        const psB = PRESCREEN_ENGAGE_ORDER[b.outreachStatus ?? ''] ?? 3
        if (psA !== psB) return psA - psB
        return b.match - a.match
      })
    } else if (sortKey === 'match') {
      list = [...list].sort((a, b) => sortDir === 'desc' ? b.match - a.match : a.match - b.match)
    } else if (sortKey === 'scoreBand') {
      list = [...list].sort((a, b) => {
        const diff = (SCORE_BAND_ORDER[a.scoreBand ?? ''] ?? 3) - (SCORE_BAND_ORDER[b.scoreBand ?? ''] ?? 3)
        return sortDir === 'desc' ? diff : -diff
      })
    } else if (sortKey === 'source') {
      list = [...list].sort((a, b) => {
        const diff = (SOURCE_ORDER[a.source ?? 'uploaded'] ?? 2) - (SOURCE_ORDER[b.source ?? 'uploaded'] ?? 2)
        return sortDir === 'desc' ? diff : -diff
      })
    } else if (sortKey === 'stage') {
      list = [...list].sort((a, b) => {
        const diff = (STAGE_ORDER[a.stage] ?? 4) - (STAGE_ORDER[b.stage] ?? 4)
        return sortDir === 'desc' ? diff : -diff
      })
    }
    return list
  })()

  const filteredCandidates = sortedFiltered
  const rejectedCandidates = candidates.filter((c) => c.rejected)

  // Source tab counts
  const sourcedCount  = activeCandidates.filter((c) => c.source === 'sourced').length
  const appliedCount  = activeCandidates.filter((c) => c.source === 'applied').length
  const uploadedCount = activeCandidates.filter((c) => !c.source || c.source === 'uploaded').length

  // Identified tile: count candidates at identified/prepared/applied stage (all map to 'Identified' locally)
  const identifiedCount = activeCandidates.filter((c) => c.stage === 'Identified').length
  const identifiedNewCount = activeCandidates.filter((c) => c.stage === 'Identified' && isNewCandidate(c.createdAt)).length

  // Toggle stage filter — clicking an already-active tile resets to 'all'
  const handleTileClick = (tile: 'identified' | 'shortlisted' | 'interview') => {
    setStageFilter((prev) => (prev === tile ? 'all' : tile))
  }

  // Sort icon helper
  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={11} className="text-gray-600 ml-1 inline" />
    return sortDir === 'desc'
      ? <ChevronDown size={11} className="text-[#FD802E] ml-1 inline" />
      : <ChevronUp   size={11} className="text-[#FD802E] ml-1 inline" />
  }

  return (
    <div className="bg-[#02182B] min-h-screen">

      <ExportModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        content={exportContent}
        title="Project Outputs"
      />

      {/* Project header */}
      <div className="border-b border-[#233D4C] bg-[#233D4C]/30">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-white">VP Sales, AlphaGrid</h1>
                <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full text-xs font-medium">
                  Complete ✓
                </span>
              </div>
              <p className="text-gray-400 text-sm mt-1">AlphaGrid · Chief Revenue Officer · EMEA</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* List / Board view toggle */}
              <div className="flex items-center gap-1 bg-[#233D4C] rounded-lg p-1 border border-[#2a4a5c]">
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FD802E] text-[#233D4C] rounded-md text-sm font-medium">
                  <LayoutGrid size={14} /> List
                </span>
                <Link
                  to={`/project/${projectId}/pipeline`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-gray-400 hover:text-white rounded-md transition-colors text-sm font-medium"
                >
                  <LayoutGrid size={14} /> Board
                </Link>
              </div>
              <Link
                to={`/project/${projectId}/outreach`}
                className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#FD802E] hover:text-[#233D4C] hover:border-[#FD802E] transition-colors"
              >
                <MessageSquare size={15} /> Outreach
              </Link>
              <button
                onClick={() => setShowExport(true)}
                className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2a4a5c] transition-colors"
              >
                <Download size={15} /> Export
              </button>
              <Link
                to="/settings"
                className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2a4a5c] transition-colors"
              >
                <Settings size={15} /> Settings
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error ? (
          <ErrorFallback onRetry={() => refetch()} />
        ) : (
          <>
          {/* Candidates loading state */}
          {candidatesLoading && (
            <div className="flex items-center justify-center gap-3 py-6 text-gray-400">
              <Loader2 size={18} className="animate-spin text-[#FD802E]" />
              <span className="text-sm">Loading candidates…</span>
            </div>
          )}

          {/* Candidates error banner */}
          {!candidatesLoading && candidatesError && (
            <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 mb-6">
              <AlertCircle size={16} className="text-red-400 shrink-0" />
              <p className="text-red-400 text-sm flex-1">Unable to load candidates. Try refreshing.</p>
              <button
                onClick={() => setCandidateListKey((k) => k + 1)}
                className="text-xs text-gray-300 hover:text-white border border-[#2a4a5c] px-3 py-1.5 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Track B — Headhunt panel (always visible on dashboard) */}
          <HeadhuntPanel
            projectId={projectId}
            onCandidatesAdded={handleCandidatesAdded}
            onCandidatesCleared={handleCandidatesAdded}
          />

          {/* Search in progress */}
          {searchStatus === 'in_progress' && (
            <div className="bg-[#233D4C] border border-blue-500/20 rounded-xl p-5 mb-6 flex items-center gap-4">
              <div className="w-9 h-9 bg-blue-500/10 rounded-lg flex items-center justify-center shrink-0">
                <Search size={16} className="text-blue-400 animate-pulse" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Search in progress</h3>
                <p className="text-gray-400 text-xs mt-0.5">Erica is searching and ranking candidates. This may take a moment.</p>
              </div>
            </div>
          )}

          {/* Start Outreach CTA — search complete, candidates loaded */}
          {(searchStatus === 'complete' || searchStatus === 'failed') && activeCandidates.length > 0 && (
            <div className="bg-[#233D4C] border border-green-500/20 rounded-xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-500/10 rounded-lg flex items-center justify-center shrink-0">
                  <CheckCircle size={16} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">{activeCandidates.length} candidates ready</h3>
                  <p className="text-gray-400 text-xs mt-0.5">Select candidates below and start outreach to begin the next phase.</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/project/${projectId}/outreach`)}
                className="shrink-0 bg-[#FD802E] text-[#233D4C] font-semibold px-5 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm flex items-center gap-2"
              >
                <MessageSquare size={14} /> Start Outreach
              </button>
            </div>
          )}

          {/* Project meta bar — compact, secondary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <Clock size={11} />
              <span>Created Mar 16, 2026</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={11} />
              <span className="text-[#FD802E] font-semibold">{SEARCH_OPEN_DAYS}</span>
              <span>days open</span>
            </div>
            <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full">Active</span>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-gray-600">Team:</span>
              {['Sax', 'Nadia', 'Grant'].map((m) => (
                <div key={m} className="w-5 h-5 bg-[#FD802E]/10 rounded-full flex items-center justify-center text-[#FD802E] text-[10px] font-bold" title={m}>{m[0]}</div>
              ))}
              <button className="text-gray-600 hover:text-[#FD802E] transition-colors ml-1"><UserPlus size={12} /></button>
            </div>
          </div>

          <div className="space-y-6">

            {/* ── Main — full width ───────────────────────────────────── */}
            <div className="space-y-6">

              {/* Pipeline stage cards — counts derived from live candidate state */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PIPELINE_STAGE_META.map((stage) => {
                  const isOutreach    = stage.label === 'Outreach'
                  const isIdentified  = stage.label === 'Identified'
                  const isShortlisted = stage.label === 'Shortlisted'
                  const isInterview   = stage.label === 'Interview'

                  // Count: Identified uses pre-computed count (includes prepared/applied); others use stage match
                  const count = isIdentified
                    ? identifiedCount
                    : activeCandidates.filter((c) => c.stage === stage.label).length

                  // Determine which stageFilter key this tile maps to
                  const tileFilter = isIdentified ? 'identified' : isShortlisted ? 'shortlisted' : isInterview ? 'interview' : null
                  const isActive = tileFilter !== null && stageFilter === tileFilter

                  if (isOutreach) {
                    return (
                      <div
                        key={stage.label}
                        onClick={() => navigate(`/project/${projectId}/outreach`)}
                        className={`bg-[#233D4C] border ${stage.border} rounded-xl p-4 flex flex-col gap-2 cursor-pointer hover:bg-[#2a4a5c] transition-colors`}
                      >
                        <div className={`w-8 h-8 ${stage.bg} rounded-lg flex items-center justify-center`}>
                          <stage.icon size={15} className={stage.text} />
                        </div>
                        <p className={`text-2xl font-bold ${stage.text}`}>{count}</p>
                        <div>
                          <p className="text-white text-xs font-semibold">{stage.label}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{stage.desc}</p>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={stage.label}
                      onClick={() => tileFilter && handleTileClick(tileFilter)}
                      className={`bg-[#233D4C] border ${stage.border} rounded-xl p-4 flex flex-col gap-2 cursor-pointer hover:bg-[#2a4a5c] transition-colors ${
                        isActive ? 'ring-2 ring-[#FD802E]' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 ${stage.bg} rounded-lg flex items-center justify-center`}>
                        <stage.icon size={15} className={stage.text} />
                      </div>
                      <div>
                        <p className={`text-2xl font-bold ${stage.text}`}>{count}</p>
                        {isIdentified && identifiedNewCount > 0 && (
                          <p className="text-green-400 text-xs font-medium mt-0.5">{identifiedNewCount} new</p>
                        )}
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold">{stage.label}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{stage.desc}</p>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Print styles — injected into document head only while present in DOM */}
              <style>{`
                @media print {
                  body * { visibility: hidden; }
                  .print-shortlist, .print-shortlist * { visibility: visible; }
                  .print-shortlist { position: absolute; left: 0; top: 0; width: 100%; }
                }
              `}</style>

              {/* Candidate table */}
              <div className="print-shortlist bg-[#233D4C] border border-[#2a4a5c] rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a4a5c]">
                  <h3 className="text-white font-semibold">
                    Candidates
                    <span className="ml-2 text-gray-500 font-normal text-sm">({filteredCandidates.length})</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    {/* Export Shortlist dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setShowExportShortlist((o) => !o)}
                        disabled={filteredCandidates.length === 0}
                        className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Download size={13} /> Export <ChevronDown size={11} />
                      </button>
                      {showExportShortlist && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowExportShortlist(false)} />
                          <div className="absolute right-0 top-full mt-1 z-20 bg-[#1a2e3d] border border-[#2a4a5c] rounded-lg py-1 min-w-[160px] shadow-xl">
                            <button
                              onClick={exportAsCSV}
                              className="w-full text-left px-4 py-2.5 text-sm text-white flex items-center gap-2 hover:bg-[#233D4C] transition-colors"
                            >
                              <FileText size={13} className="text-green-400" /> Export as CSV
                            </button>
                            <button
                              onClick={exportAsPDF}
                              className="w-full text-left px-4 py-2.5 text-sm text-white flex items-center gap-2 hover:bg-[#233D4C] transition-colors"
                            >
                              <Download size={13} className="text-blue-400" /> Export as PDF
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Hidden CV file input */}
                    <input
                      ref={cvInputRef}
                      type="file"
                      accept=".pdf,.docx,.doc"
                      className="hidden"
                      onChange={handleCvFileSelected}
                    />
                    {/* CV Toast */}
                    {cvToast && (
                      <span className={`text-xs font-medium ${
                        cvToast.type === 'success' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {cvToast.message}
                      </span>
                    )}
                    {/* Upload CV button */}
                    <button
                      onClick={() => cvInputRef.current?.click()}
                      disabled={cvUploading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#233D4C] border border-[#2a4a5c] text-white hover:border-[#FD802E]/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Upload a PDF or Word CV to add a candidate"
                    >
                      {cvUploading
                        ? <><Loader2 size={13} className="animate-spin" /> Reading CV…</>
                        : <><Upload size={13} /> Upload CV</>
                      }
                    </button>

                  </div>
                </div>

                {/* Source filter tabs — hidden when a stage filter is active */}
                {stageFilter === 'all' && (
                <div className="flex items-center gap-1 px-5 py-2.5 border-b border-[#2a4a5c] overflow-x-auto">
                  {(
                    [
                      { key: 'all',      label: 'All',      count: activeCandidates.length },
                      { key: 'sourced',  label: 'Sourced',  count: sourcedCount },
                      { key: 'applied',  label: 'Applied',  count: appliedCount },
                      { key: 'uploaded', label: 'Uploaded', count: uploadedCount },
                    ] as const
                  ).map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => setSourceFilter(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                        sourceFilter === key
                          ? 'bg-[#FD802E]/15 text-[#FD802E] border border-[#FD802E]/30'
                          : 'text-gray-400 hover:text-white hover:bg-[#1a3347]'
                      }`}
                    >
                      {label}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        sourceFilter === key ? 'bg-[#FD802E]/20 text-[#FD802E]' : 'bg-[#1a3347] text-gray-500'
                      }`}>
                        {count}
                      </span>
                    </button>
                  ))}
                </div>
                )}

                {/* Bulk action bar — appears when rows are selected */}
                {selected.size > 0 && (
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-[#1a3347] border-b border-[#FD802E]/30">
                    <span className="text-[#FD802E] text-xs font-semibold">{selected.size} selected</span>
                    <div className="flex items-center gap-2 ml-auto">
                      {/* Move stage */}
                      <div className="relative">
                        <button
                          onClick={() => setBulkStageOpen((o) => !o)}
                          disabled={bulkWorking}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#233D4C] border border-[#2a4a5c] text-white text-xs rounded-lg hover:bg-[#2a4a5c] transition-colors disabled:opacity-50"
                        >
                          {bulkWorking ? <Loader2 size={11} className="animate-spin" /> : <MoveRight size={11} />}
                          Move stage <ChevronDown size={10} />
                        </button>
                        {bulkStageOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setBulkStageOpen(false)} />
                            <div className="absolute right-0 top-full mt-1 z-20 bg-[#1a2e3d] border border-[#2a4a5c] rounded-lg py-1 min-w-[140px] shadow-xl">
                              {STAGE_LABELS.map((s) => (
                                <button
                                  key={s}
                                  onClick={() => bulkMoveStage(s)}
                                  className="w-full text-left px-3 py-2 text-xs text-white flex items-center gap-2 hover:bg-[#233D4C] transition-colors"
                                >
                                  <span className={`w-2 h-2 rounded-full shrink-0 ${
                                    s === 'Identified' ? 'bg-[#FD802E]' : s === 'Outreach' ? 'bg-blue-400' :
                                    s === 'Shortlisted' ? 'bg-yellow-400' : 'bg-green-400'
                                  }`} />
                                  {s}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {/* Delete */}
                      {!confirmBulkDel ? (
                        <button
                          onClick={() => setConfirmBulkDel(true)}
                          disabled={bulkWorking}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 text-xs rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-red-400 text-xs">Remove {selected.size} candidates?</span>
                          <button onClick={bulkDelete} className="px-2.5 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors">
                            Confirm
                          </button>
                          <button onClick={() => setConfirmBulkDel(false)} className="px-2.5 py-1 bg-[#233D4C] text-gray-300 text-xs rounded-lg hover:text-white transition-colors">
                            Cancel
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => setSelected(new Set())}
                        className="text-gray-500 hover:text-gray-300 text-xs px-2 py-1.5 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                {/* Table header */}
                <div className="hidden sm:grid grid-cols-[2rem_3fr_2fr_1.5fr_3rem_5rem_4rem_3rem_6rem_6rem_5rem] gap-2 px-5 py-2.5 border-b border-[#2a4a5c] text-xs uppercase text-gray-500 font-medium">
                  {/* Select-all checkbox */}
                  <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={filteredCandidates.length > 0 && filteredCandidates.every((c) => selected.has(c.id))}
                      onChange={() => toggleSelectAll(filteredCandidates.map((c) => c.id))}
                      className="w-3.5 h-3.5 rounded accent-[#FD802E] cursor-pointer"
                    />
                  </div>
                  <span>Candidate</span>
                  <span>Current Role</span>
                  <span>Location</span>
                  <button
                    onClick={() => handleSort('match')}
                    className="flex items-center justify-center hover:text-white transition-colors cursor-pointer"
                  >
                    Match<SortIcon col="match" />
                  </button>
                  <button
                    onClick={() => handleSort('scoreBand')}
                    className="flex items-center justify-center hover:text-white transition-colors cursor-pointer"
                  >
                    Score<SortIcon col="scoreBand" />
                  </button>
                  <button
                    onClick={() => handleSort('source')}
                    className="flex items-center justify-center hover:text-white transition-colors cursor-pointer"
                  >
                    Source<SortIcon col="source" />
                  </button>
                  <span className="text-center">Prescreen</span>
                  <span className="text-center">CV</span>
                  <button
                    onClick={() => handleSort('stage')}
                    className="flex items-center justify-center hover:text-white transition-colors cursor-pointer"
                  >
                    Stage<SortIcon col="stage" />
                  </button>
                  <span />
                </div>

                {/* Active rows */}
                <ul className="divide-y divide-[#2a4a5c]">
                  {filteredCandidates.length === 0 && (
                    <li className="px-5 py-8 text-center text-gray-500 text-sm">
                      {stageFilter === 'identified'
                        ? 'No candidates at this stage yet.'
                        : stageFilter === 'shortlisted'
                        ? 'No candidates shortlisted yet. Approve candidates from the pipeline to move them here.'
                        : stageFilter === 'interview'
                        ? 'No candidates at interview stage yet.'
                        : `No ${sourceFilter === 'all' ? '' : sourceFilter + ' '}candidates yet.`}
                    </li>
                  )}
                  {filteredCandidates.map((c) => (
                    <li key={c.id} className={`relative ${selected.has(c.id) ? 'bg-[#1a3347]/60' : ''}`}>

                      {/* Reject confirm overlay */}
                      {rejectConfirm === c.id && (
                        <div
                          className="absolute inset-0 z-10 bg-[#02182B]/95 flex items-center justify-center gap-4 px-5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="text-white text-sm flex-1">
                            Reject <span className="font-semibold text-[#FD802E]">{c.name}</span>?
                            <span className="block text-gray-400 text-xs mt-0.5">Candidate will be moved to the rejected section.</span>
                          </p>
                          <button onClick={() => rejectCandidate(c.id)} className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium rounded-lg hover:bg-red-500/20 transition-colors">
                            Confirm Reject
                          </button>
                          <button onClick={() => setRejectConfirm(null)} className="px-3 py-1.5 bg-[#233D4C] border border-[#2a4a5c] text-gray-300 text-xs rounded-lg hover:text-white transition-colors">
                            Cancel
                          </button>
                        </div>
                      )}

                      <div className="grid sm:grid-cols-[2rem_3fr_2fr_1.5fr_3rem_5rem_4rem_3rem_6rem_6rem_5rem] gap-2 items-center px-5 py-4 hover:bg-[#02182B]/50 transition-colors group">
                        {/* Row checkbox */}
                        <div className="hidden sm:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            className="w-3.5 h-3.5 rounded accent-[#FD802E] cursor-pointer"
                          />
                        </div>

                        {/* Avatar + name + LinkedIn — clickable to profile */}
                        <div
                          className="flex items-center gap-3 min-w-0 cursor-pointer"
                          onClick={() => navigate(`/project/${projectId}/candidate/${c.id}`)}
                        >
                          <div className="w-9 h-9 bg-[#FD802E] rounded-full flex items-center justify-center text-[#233D4C] font-bold text-xs shrink-0">
                            {c.initials}
                          </div>
                          <div className="min-w-0">
                            <span className="text-white text-sm font-medium truncate group-hover:text-[#FD802E] transition-colors block flex items-center gap-1.5">
                              {c.name}
                              {isNewCandidate(c.createdAt) && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/15 text-green-400 border border-green-500/30 shrink-0">New</span>
                              )}
                            </span>
                            {c.linkedinUrl && (
                              <a
                                href={c.linkedinUrl.startsWith('http') ? c.linkedinUrl : `https://${c.linkedinUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[11px] text-blue-400 hover:text-blue-300 hover:underline transition-colors mt-0.5 block"
                              >
                                View LinkedIn
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Title + company */}
                        <div
                          className="min-w-0 hidden sm:block cursor-pointer"
                          onClick={() => navigate(`/project/${projectId}/candidate/${c.id}`)}
                        >
                          <p className="text-white text-sm truncate">{c.title || '—'}</p>
                          {c.company && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Building2 size={10} className="text-gray-500 shrink-0" />
                              <p className="text-gray-400 text-xs truncate">{c.company}</p>
                            </div>
                          )}
                        </div>

                        {/* Location */}
                        <div className="hidden sm:flex items-center gap-1.5 min-w-0">
                          {c.location ? (
                            <>
                              <MapPin size={11} className="text-[#FD802E]/60 shrink-0" />
                              <span className="text-gray-300 text-xs truncate">{c.location}</span>
                            </>
                          ) : (
                            <span className="text-gray-600 text-xs">—</span>
                          )}
                        </div>

                        {/* Match */}
                        <div className="hidden sm:flex justify-center">
                          <MatchBadge score={c.match} />
                        </div>

                        {/* Score band */}
                        <div className="hidden sm:flex justify-center">
                          <ScoreBandBadge band={c.scoreBand} />
                        </div>

                        {/* Source */}
                        <div className="hidden sm:flex justify-center">
                          <SourceBadge source={c.source ?? 'uploaded'} />
                        </div>

                        {/* Prescreen */}
                        <div className="hidden sm:flex justify-center">
                          {(() => {
                            const ps = getPreScreenInfo(c.outreachStatus)
                            return <span className={ps.className}>{ps.label}</span>
                          })()}
                        </div>

                        {/* CV icon */}
                        <div className="hidden sm:flex justify-center" onClick={(e) => e.stopPropagation()}>
                          {c.cvUrl ? (
                            <a
                              href={c.cvUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View CV"
                              className="text-[#FD802E] hover:text-[#ff8f45] transition-colors"
                            >
                              <FileText size={14} />
                            </a>
                          ) : (
                            <span title="No CV uploaded" className="text-gray-600 cursor-default">
                              <FileText size={14} />
                            </span>
                          )}
                        </div>

                        {/* Stage selector */}
                        <div className="hidden sm:flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <StageSelector
                            current={c.stage}
                            onChange={(stage) => moveStage(c.id, stage)}
                          />
                        </div>

                        {/* Approve / Reject buttons */}
                        <div className="hidden sm:flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => approveForOutreach(c.id)}
                            title="Approve for outreach"
                            disabled={c.stage === 'Outreach'}
                            className="bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 rounded-lg p-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Check size={13} />
                          </button>
                          <button
                            onClick={() => setRejectConfirm(c.id)}
                            title="Reject candidate"
                            className="bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg p-1.5 transition-colors"
                          >
                            <X size={13} />
                          </button>
                        </div>

                        {/* Mobile compact */}
                        <div className="flex sm:hidden items-center justify-between col-span-full ml-12 -mt-2">
                          <div>
                            <p className="text-gray-400 text-xs">{c.title} · {c.company}</p>
                            <p className="text-gray-500 text-xs mt-0.5">{c.location}</p>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <span className={`text-sm font-bold ${c.match >= 85 ? 'text-green-400' : c.match >= 70 ? 'text-[#FD802E]' : 'text-gray-400'}`}>
                              {c.match}%
                            </span>
                            <StageSelector current={c.stage} onChange={(stage) => moveStage(c.id, stage)} />
                            <button
                              onClick={() => approveForOutreach(c.id)}
                              disabled={c.stage === 'Outreach'}
                              className="bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 rounded-lg p-1.5 transition-colors disabled:opacity-30"
                            >
                              <Check size={12} />
                            </button>
                            <button onClick={() => setRejectConfirm(c.id)} className="bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded-lg p-1.5 transition-colors">
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Rejected section */}
                {rejectedCandidates.length > 0 && (
                  <div className="border-t border-[#2a4a5c]">
                    <div className="px-5 py-2.5">
                      <p className="text-xs uppercase text-gray-500 font-medium">Rejected ({rejectedCandidates.length})</p>
                    </div>
                    <ul className="divide-y divide-[#2a4a5c]">
                      {rejectedCandidates.map((c) => (
                        <li key={c.id} className="flex items-center gap-3 px-5 py-3 opacity-50">
                          <div className="w-7 h-7 bg-gray-500/20 rounded-full flex items-center justify-center text-gray-500 font-bold text-xs shrink-0">
                            {c.initials}
                          </div>
                          <span className="text-gray-500 text-sm line-through">{c.name}</span>
                          <span className="ml-auto px-2 py-0.5 bg-red-500/10 text-red-400 rounded-full text-xs">Rejected</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Generated outputs */}
              <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Generated Outputs</h3>
                  <button
                    onClick={() => navigate(`/project/${projectId}/output`)}
                    className="text-[#FD802E] hover:text-[#ff8f45] text-sm transition-colors flex items-center gap-1"
                  >
                    View all <ArrowRight size={13} />
                  </button>
                </div>
                <ul className="space-y-2">
                  {OUTPUTS.map((output) => (
                    <li key={output} className="flex items-center justify-between bg-[#02182B] border border-[#2a4a5c] rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-green-400" />
                        <span className="text-white text-sm">{output}</span>
                      </div>
                      <button
                        onClick={() => setShowExport(true)}
                        className="text-gray-400 hover:text-[#FD802E] transition-colors"
                        title="Export"
                      >
                        <Download size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* ── Sidebar (1 col) ───────────────────────────────────────── */}
          </div>
          </>
        )}
      </div>
    </div>
  )
}
